import { filterByMatchingAttributes } from "../../hooks/useStandardProducts";
import {
  getProductSaveErrorMessage,
  NameAndSkuEditor,
  PRODUCT_NAME_REQUIRED_ERROR,
  PRODUCT_SKU_REQUIRED_ERROR,
} from "./NameAndSkuEditor";
import { useProductCreateContext } from "./CustomerProductCreateView";
import { attributesForFiltering, ProductCreateNavButtons, StepProps } from "./ProductCreateNavigator";

export function NameAndSkuStep({ state, setState, standardProducts, visibleSteps }: StepProps) {
  const { onSave, loading } = useProductCreateContext();

  const filteredProducts = filterByMatchingAttributes(standardProducts, attributesForFiltering(state, visibleSteps));
  if (filteredProducts.length !== 1) {
    throw new Error(`Product create filter didn't resolve to a single openapi SKU -  ${filteredProducts}`);
  }
  const openapiProduct = filteredProducts[0];

  const onDone = () => {
    setState((draft) => {
      const nameField = draft.customerProductName;
      const skuField = getSkuValue(openapiProduct.openapiSku, draft.customerProductSku);
      if (!nameField || !skuField) {
        draft.nextFailed = true;
      } else {
        draft.nextFailed = false;

        onSave(nameField, skuField, openapiProduct.id, (error) => {
          const message = getProductSaveErrorMessage(error);
          if (message) {
            setState((draft) => {
              draft.saveError = message;
              draft.nextFailed = true;
            });
          }
        });
      }
    });
  };

  const nameError = state.nextFailed && !state.customerProductName ? PRODUCT_NAME_REQUIRED_ERROR : undefined;
  const skuValue = getSkuValue(openapiProduct.openapiSku, state.customerProductSku);
  const skuEmptyError = state.nextFailed && !skuValue ? PRODUCT_SKU_REQUIRED_ERROR : undefined;

  const handleProductNameChanged = (newProductName: string) =>
    setState((draft) => {
      draft.customerProductName = newProductName;
      draft.nextFailed = false;
    });

  const handleCustomerSkuChanged = (newSku: string) =>
    setState((draft) => {
      draft.saveError = "";
      draft.customerProductSku = newSku;
      draft.nextFailed = false;
    });

  return (
    <>
      <NameAndSkuEditor
        openapiProduct={openapiProduct}
        productName={state.customerProductName}
        productNameError={nameError}
        onProductNameChanged={handleProductNameChanged}
        customerSku={skuValue}
        customerSkuError={state.saveError || skuEmptyError}
        onCustomerSkuChanged={handleCustomerSkuChanged}
        loading={loading}
      />
      <ProductCreateNavButtons
        state={state}
        setState={setState}
        visibleSteps={visibleSteps}
        onDone={onDone}
        loading={loading}
      />
    </>
  );
}

// this defaults the openapiSku when the customer sku is undefined,
// but still returns empty string if customer sku has been set to that by the user
function getSkuValue(openapiSku: string, customerSku?: string) {
  return customerSku ?? openapiSku;
}
